import React from "react";
import { Box, Typography } from "@mui/joy";

export function renderInlineBold(text: string) {
  const nodes: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g; // **bold**
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    nodes.push(
      <Box
        key={`b-${match.index}-${match[1]}`}
        component="span"
        sx={{ fontWeight: 700 }}
      >
        {match[1]}
      </Box>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  // If no matches, return original string for performance
  return nodes.length ? nodes : [text];
}

function parseTable(lines: string[], startIndex: number) {
  // Collect consecutive lines starting with '|'
  const tableLines: string[] = [];
  let i = startIndex;
  while (i < lines.length && lines[i].trim().startsWith("|")) {
    tableLines.push(lines[i]);
    i++;
  }
  if (tableLines.length < 2) {
    return { node: null as React.ReactNode | null, nextIndex: startIndex };
  }

  // Expect header separator on second line (contains '-' and '|')
  const headerLine = tableLines[0];
  const separatorLine = tableLines[1];
  if (!/\|\s*-{3,}\s*/.test(separatorLine)) {
    // Not a valid table; treat as normal text
    return { node: null as React.ReactNode | null, nextIndex: startIndex };
  }

  const splitRow = (row: string) => {
    const raw = row.split("|");
    // If line starts with '|', first token is empty
    if (raw.length && raw[0].trim() === "") raw.shift();
    // If line ends with '|', last token is empty
    if (raw.length && raw[raw.length - 1].trim() === "") raw.pop();
    return raw.map((c) => c.trim());
  };

  const headers = splitRow(headerLine);
  const bodyLines = tableLines.slice(2);
  const rows = bodyLines.map(splitRow);

  const tableNode = (
    <Box
      key={`tbl-${startIndex}`}
      sx={{ width: "100%", overflowX: "auto", my: 1.5 }}
    >
      <Box
        component="table"
        sx={{
          width: "100%",
          minWidth: 360,
          borderCollapse: "collapse",
        }}
      >
        <Box component="thead">
          <Box component="tr">
            {headers.map((h, idx) => (
              <Box
                key={`th-${idx}`}
                component="th"
                sx={{
                  textAlign: "left",
                  border: (t) => `1px solid ${t.vars.palette.divider}`,
                  backgroundColor: (t) => t.vars.palette.Background.Level1,
                  px: 1,
                  py: 0.75,
                  fontWeight: 700,
                  whiteSpace: "pre-wrap",
                }}
              >
                <Typography variant="Body">{h}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((cells, rIdx) => (
            <Box key={`tr-${rIdx}`} component="tr">
              {cells.map((c, cIdx) => (
                <Box
                  key={`td-${rIdx}-${cIdx}`}
                  component="td"
                  sx={{
                    border: (t) => `1px solid ${t.vars.palette.divider}`,
                    px: 1,
                    py: 0.75,
                    verticalAlign: "top",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <Typography variant="Body">{c}</Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  return { node: tableNode, nextIndex: startIndex + tableLines.length };
}

export default function SimpleMdToTypo({ source }: { source: string }) {
  const lines = source.split(/\r?\n/);
  const nodes: React.ReactNode[] = [];

  let i = 0;
  let paraLines: string[] = [];

  const flushParagraph = () => {
    if (paraLines.length === 0) return;
    const text = paraLines.join("\n");
    nodes.push(
      <Typography key={`p-${i}-${nodes.length}`} variant="Body" sx={{ my: 1 }}>
        {renderInlineBold(text)}
      </Typography>
    );
    paraLines = [];
  };

  while (i < lines.length) {
    const raw = lines[i];
    const line = raw; // keep original including hashes and dashes
    const trimmed = line.trim();

    // Try table
    if (trimmed.startsWith("|")) {
      flushParagraph();
      const { node, nextIndex } = parseTable(lines, i);
      if (node) {
        nodes.push(node);
        i = nextIndex;
        continue;
      }
      // fall through if not a valid table
    }

    // Heading H3: lines starting with '### '
    if (trimmed.startsWith("### ") || /^###[^#\s]/.test(trimmed)) {
      flushParagraph();
      const content = line.replace(/^###\s*/, "");
      nodes.push(
        <Typography
          key={`h3-${i}`}
          variant="H3"
          sx={{ mt: 2, mb: 0.75, fontWeight: 700 }}
        >
          {renderInlineBold(content)}
        </Typography>
      );
      i++;
      continue;
    }

    // Heading H2: lines starting with '## ' (double #)
    if (trimmed.startsWith("## ") || /^##[^#\s]/.test(trimmed)) {
      flushParagraph();
      const content = line.replace(/^##\s*/, "");
      nodes.push(
        <Typography
          key={`h2-${i}`}
          variant="H2"
          sx={{ mt: 2, mb: 0.75, fontWeight: 700 }}
        >
          {renderInlineBold(content)}
        </Typography>
      );
      i++;
      continue;
    }

    // Heading H1: lines starting with '# ' (single #)
    if (trimmed.startsWith("# ") || /^#[^#\s]/.test(trimmed)) {
      flushParagraph();
      const content = line.replace(/^#\s*/, "");
      nodes.push(
        <Typography
          key={`h1-${i}`}
          variant="H1"
          sx={{ mt: 2, mb: 0.75, fontWeight: 700 }}
        >
          {renderInlineBold(content)}
        </Typography>
      );
      i++;
      continue;
    }

    // Blank line → paragraph break
    if (trimmed === "") {
      flushParagraph();
      i++;
      continue;
    }

    // Default: collect into paragraph
    paraLines.push(line);
    i++;
  }

  // Flush remaining paragraph
  flushParagraph();

  return <Box>{nodes}</Box>;
}
